import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Tag, ChevronLeft, Share2, Bookmark, User, CatIcon, Github, FileText, Linkedin, Mail, ExternalLink, Menu, X, Shield, Code, Server, Cpu, Instagram, MessageCircle, Briefcase, Send, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import blogData from '../../components/blogData.json';
// Import these additional packages for better markdown support
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import {nightOwl} from 'react-syntax-highlighter/dist/esm/styles/prism';
import LoadingScreen from '../../components/LoadingScreen';

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [markdownContent, setMarkdownContent] = useState('');
  const [relatedPosts, setRelatedPosts] = useState([]);
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const revealRef = useScrollReveal(0.1);
  
  // Navigation state from Portfolio component
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  // State for copy button
  const [copiedCode, setCopiedCode] = useState('');

  // Navigation links from Portfolio
  const navLinks = [
    { name: "About", href: "/#about", icon: <User size={18} /> },
    { name: "Skills", href: "/#skills", icon: <Code size={18} /> },
    { name: "Projects", href: "/#projects", icon: <Briefcase size={18} /> },
    { name: "Blog", href: "/blog", icon: <FileText size={18} /> },
  ];
  useEffect(() => {
    // Simulate loading time or wait for resources
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000); // Adjust timing as needed (2 seconds in this example)
    
    return () => clearTimeout(timer);
  }, []);
  // Function to copy code to clipboard
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 3000); // Reset after 3 seconds
    });
  };
  
  useEffect(() => {
    // Simulate loading blog data
    setLoading(true);
    // Find the blog post with the matching ID
    const foundBlog = blogData.find(blog => blog.id === slug);
    
    if (foundBlog) {
      setBlog(foundBlog);
      
      // Find related posts that share at least one tag
      const related = blogData
        .filter(post => post.id !== slug && post.tags.some(tag => foundBlog.tags.includes(tag)))
        .slice(0, 3);
      
      setRelatedPosts(related);
      
      // Load markdown content from slug.md file
      fetch(`/markdown/${slug}.md`)
        .then(response => {
          if (!response.ok) {
            throw new Error('Failed to load markdown file');
          }
          return response.text();
        })
        .then(data => {
          setMarkdownContent(data);
          setLoading(false);
        })
        .catch(error => {
          console.error('Error loading markdown file:', error);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    
    // Scroll to top when blog post changes
    window.scrollTo(0, 0);
  }, [slug]);

  // Navbar scroll effect from Portfolio
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setShowNavbar(false);

        if (isMenuOpen) {
          setIsMenuOpen(false);
        }
      } else {
        setShowNavbar(true);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY, isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  if (loading) {
    return (
      <LoadingScreen />
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-900 text-gray-100 pt-20 flex flex-col items-center justify-center">
        <h2 className="text-2xl font-bold mb-4">Blog post not found</h2>
        <button 
          onClick={() => navigate('/blog')}
          className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white px-5 py-2 rounded-md flex items-center space-x-2"
        >
          <ChevronLeft size={18} />
          <span>Back to Blog</span>
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      {/* CSS for animations and mobile responsiveness */}
      <style jsx global>{`
        .animate-reveal {
          animation: reveal 0.8s ease forwards;
        }

        @keyframes reveal {
          0% {
            opacity: 0;
            transform: translateY(30px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .reveal-element {
          opacity: 0;
          transform: translateY(30px);
        }

        .staggered-reveal > * {
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .staggered-reveal > *:nth-child(1) { transition-delay: 0.1s; }
        .staggered-reveal > *:nth-child(2) { transition-delay: 0.2s; }
        .staggered-reveal > *:nth-child(3) { transition-delay: 0.3s; }
        .staggered-reveal > *:nth-child(4) { transition-delay: 0.4s; }
        .staggered-reveal > *:nth-child(5) { transition-delay: 0.5s; }

        .animate-reveal.staggered-reveal > * {
          opacity: 1;
          transform: translateY(0);
        }

        .nav-link {
          position: relative;
          transition: all 0.3s ease;
        }

        .nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -4px;
          left: 0;
          background: linear-gradient(to right, #2dd4bf, #a855f7);
          transition: width 0.3s ease;
        }

        .nav-link:hover::after {
          width: 100%;
        }
        
        /* Code copy button styles */
        .code-wrapper {
          position: relative;
        }
        
        .copy-button {
          position: absolute;
          top: 8px;
          right: 8px;
          background-color: rgba(30, 41, 59, 0.5);
          border-radius: 4px;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          z-index: 10;
        }
        
        .copy-button:hover {
          background-color: rgba(30, 41, 59, 0.8);
        }
        
        .copy-tooltip {
          position: absolute;
          top: 20px;
          right: 0;
          background-color: #0ea5e9;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          opacity: 0;
          transform: translateY(5px);
          transition: all 0.2s ease;
          pointer-events: none;
        }
        
        .copy-button:hover .copy-tooltip {
          opacity: 1;
          transform: translateY(0);
        }
        
        /* Modified: Changed position to below the button */
        .copy-success {
          position: absolute;
          bottom: -20px; /* Changed from top to bottom */
          right: 0;
          background-color: #10b981;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          opacity: 0;
          transform: translateY(-5px); /* Changed direction */
          transition: all 0.2s ease;
          pointer-events: none;
        }
        
        .copy-success.show {
          opacity: 1;
          transform: translateY(0);
        }

        /* Fix horizontal scrolling on mobile */
        html, body {
          overflow-x: hidden;
          width: 100%;
          position: relative;
        }

        /* Responsive text sizing */
        @media (max-width: 640px) {
          .prose h1 {
            font-size: 1.5rem !important;
          }
          .prose h2 {
            font-size: 1.25rem !important;
          }
          .prose h3 {
            font-size: 1.125rem !important;
          }
          .prose p, .prose li {
            font-size: 0.95rem !important;
          }
          .prose code {
            font-size: 0.85rem !important;
          }
        }

        /* Better code block responsiveness */
        .prose pre {
          max-width: 100%;
          overflow-x: auto;
          white-space: pre-wrap;
          word-wrap: break-word;
        }

        /* Code within pre elements */
        .prose pre code {
          display: block;
          padding: 1rem;
          width: 100%;
          overflow-x: auto;
        }

        /* Improved image responsiveness */
        .prose img {
          max-width: 100%;
          height: auto;
        }

        /* Tables should not cause horizontal overflow */
        .prose table {
          width: 100%;
          display: block;
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Fix for markdown content alignment */
        .markdown-content {
          width: 100%;
          text-align: left;
          margin-left: 0;
          margin-right: 0;
        }
        
        .markdown-content > * {
          width: 100%;
          margin-left: 0 !important;
          margin-right: 0 !important;
        }
        
        /* Ensure markdown containers don't auto-center */
        .prose {
          width: 100%;
          text-align: left;
          margin-left: 0;
          margin-right: 0;
        }
      `}</style>

      {/* Navigation bar with improved mobile responsiveness */}
      <nav className={`fixed w-full z-10 bg-gray-900 bg-opacity-95 border-b border-gray-800 shadow-lg transition-transform duration-300 ${showNavbar ? 'translate-y-0' : '-translate-y-full'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <a href="/" className="flex items-center">
                <CatIcon size={20} className="text-cyan-400 mr-2" />
                <span className="font-bold text-xl text-cyan-400">ARXHR<span className="text-purple-400">007</span></span>
              </a>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4 lg:space-x-8">
              {navLinks.map((link) => (
                <a 
                  key={link.name}
                  href={link.href} 
                  className="nav-link flex items-center space-x-2 text-gray-300 hover:text-cyan-400 transition-colors duration-300"
                >
                  {link.icon}
                  <span>{link.name}</span>
                </a>
              ))}
              <a 
                href="/#contact" 
                className="bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-600 hover:to-purple-600 text-white px-4 py-2 rounded-md flex items-center space-x-2 transition-all duration-300 shadow-lg hover:shadow-cyan-500/20"
              >
                <Send size={16} />
                <span>Contact</span>
              </a>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button 
                onClick={toggleMenu} 
                className="text-gray-300 hover:text-white focus:outline-none p-2"
                aria-label="Toggle menu"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu - Improved */}
        <div 
          className={`md:hidden bg-gray-800 transition-all duration-300 ease-in-out overflow-hidden ${
            isMenuOpen ? 'max-h-70 opacity-100 visible' : 'max-h-0 opacity-0 invisible'
          }`}
        >
          <div className="px-4 pt-2 pb-3">
            {navLinks.map((link) => (
              <a 
                key={link.name}
                href={link.href} 
                className="flex items-center space-x-3 py-3 px-2 hover:bg-gray-700 rounded-md text-gray-300 hover:text-cyan-400 transition-all duration-200" 
                onClick={toggleMenu}
              >
                {link.icon}
                <span>{link.name}</span>
              </a>
            ))}
            <a 
              href="/#contact" 
              className="flex items-center space-x-3 py-3 px-2 mt-2 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-md text-white transition-all duration-200"
              onClick={toggleMenu}
            >
              <Send size={18} />
              <span>Contact</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Main content with improved mobile responsiveness */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-12">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/blog')}
          className="mb-6 text-gray-300 hover:text-cyan-400 transition-colors duration-300 flex items-center"
        >
          <ChevronLeft size={18} className="mr-1" />
          <span>Back to Blog</span>
        </button>
        
        {/* Article Header */}
        <div ref={revealRef} className="mb-6 sm:mb-8 reveal-element">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-purple-500 text-transparent bg-clip-text">
            {blog.title}
          </h1>
          
          <div className="flex flex-wrap gap-3 sm:gap-4 mb-4 sm:mb-6">
            <span className="text-xs sm:text-sm text-gray-400 flex items-center">
              <Calendar size={14} className="mr-1 text-cyan-400" />
              {blog.date}
            </span>
            <span className="text-xs sm:text-sm text-gray-400 flex items-center">
              <Clock size={14} className="mr-1 text-purple-400" />
              {blog.readTime} min read
            </span>
            <span className="text-xs sm:text-sm text-gray-400 flex items-center">
              <User size={14} className="mr-1 text-green-400" />
              {blog.author}
            </span>
          </div>
          
          <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
            {blog.tags.map(tag => (
              <span key={tag} className="bg-gray-800 text-cyan-400 text-xs px-2 sm:px-3 py-1 rounded-full flex items-center">
                <Tag size={10} className="mr-1" />
                {tag}
              </span>
            ))}
          </div>
        </div>
        
        {/* Featured Image */}
        <div ref={revealRef} className="mb-6 sm:mb-8 reveal-element">
          <div className="rounded-lg overflow-hidden shadow-lg">
            <img
              src={blog.imageUrl}
              alt={blog.title}
              className="w-full h-auto object-cover"
              loading="lazy"
            />
          </div>
        </div>
        

        {/* Article Content with improved alignment and code copy button */}
        <div ref={contentRef} className="prose prose-invert prose-cyan max-w-none mb-8 sm:mb-12 overflow-hidden markdown-content">
          {/* Enhanced ReactMarkdown implementation with copy button */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]} // Adds support for GitHub Flavored Markdown
            rehypePlugins={[rehypeRaw, rehypeSanitize]} // Allows HTML in markdown and sanitizes it
            components={{
              h1: ({node, ...props}) => <h1 className="text-xl sm:text-2xl font-bold my-3 sm:my-4 text-cyan-400 w-full text-left" {...props} />,
              h2: ({node, ...props}) => <h2 className="text-lg sm:text-xl font-bold my-2 sm:my-3 text-purple-400 w-full text-left" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-base sm:text-lg font-bold my-2 w-full text-left" {...props} />,
              p: ({node, ...props}) => <p className="my-3 sm:my-4 text-gray-300 text-sm sm:text-base break-words w-full text-left" {...props} />,
              a: ({node, ...props}) => <a className="text-cyan-400 hover:text-cyan-300 underline break-words" {...props} />,
              ul: ({node, ...props}) => <ul className="my-3 sm:my-4 list-disc pl-4 sm:pl-6 text-sm sm:text-base w-full text-left" {...props} />,
              ol: ({node, ...props}) => <ol className="my-3 sm:my-4 list-decimal pl-4 sm:pl-6 text-sm sm:text-base w-full text-left" {...props} />,
              li: ({node, ...props}) => <li className="my-1 text-left" {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-cyan-500 pl-3 sm:pl-4 italic my-3 sm:my-4 text-gray-400 text-sm sm:text-base w-full text-left" {...props} />,
              
              code: ({ node, inline, className, children, ...props }) => {
                const match = /language-(\w+)/.exec(className || "");
                const codeString = String(children).replace(/\n$/, '');
                
                return !inline && match ? (
                  <div className="code-wrapper relative my-4 rounded-md !p-0 !m-0">
                    <div 
                      className="copy-button" 
                      onClick={() => copyToClipboard(codeString)}
                      title="Copy code"
                    >
                      {copiedCode === codeString ? (
                        <Check size={16} className="text-green-400" />
                      ) : (
                        <Copy size={16} className="text-cyan-400" />
                      )}
                      <span className="copy-tooltip">Copy</span>
                      <span className={`copy-success ${copiedCode === codeString ? 'show' : ''}`}>
                        Copied!
                      </span>
                    </div>
                    <SyntaxHighlighter
                      style={nightOwl}
                      language={match[1]}
                      PreTag="div"
                      className="rounded-md text-sm sm:text-base overflow-x-auto w-full !p-0 !m-0"
                    >
                      {codeString}
                    </SyntaxHighlighter>
                  </div>
                ) : (
                  <code className="bg-gray-800 px-1 py-0.5 rounded text-cyan-300 text-xs sm:text-sm" {...props}>
                    {children}
                  </code>
                );
              },
              pre: ({node, ...props}) => <pre className="bg-gray-800 rounded-md my-4 overflow-auto w-full text-left" {...props} />,
              img: ({node, ...props}) => <img className="w-full h-auto rounded-md my-4" loading="lazy" {...props} />,
              table: ({node, ...props}) => <div className="overflow-x-auto my-4 w-full"><table className="min-w-full text-sm text-left" {...props} /></div>,
              div: ({node, ...props}) => <div className="w-full text-left" {...props} />,
              span: ({node, ...props}) => <span className="text-left" {...props} />,
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>

{/* Author Bio */}
<a 
  href="https://www.arxhr007.me/" 
  target="_blank" 
  rel="noopener noreferrer"
  className="block"
>
  <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-gray-700 mb-8 sm:mb-12 cursor-pointer transition hover:bg-gray-700">
    <div className="flex items-center mb-4">
      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full overflow-hidden mr-4">
        <img src="/assets/real.png" alt="Author" className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div>
        <h3 className="font-bold text-base sm:text-lg">{blog.author}</h3>
        <p className="text-xs sm:text-sm text-gray-400">Cybersecurity Enthusiast & Developer</p>
      </div>
    </div>
    <p className="text-gray-300 text-sm sm:text-base">
      Full stack developer and cybersecurity enthusiast passionate about building secure applications and sharing knowledge with the community.
    </p>
  </div>
</a>

        
        {/* Related Posts - Improved for mobile */}
        {relatedPosts.length > 0 && (
          <div className="mb-8 sm:mb-12">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-cyan-400 to-purple-500 text-transparent bg-clip-text">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
              {relatedPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-gray-800 rounded-lg overflow-hidden shadow-lg border border-gray-700 hover:border-cyan-500 transition-all duration-300 cursor-pointer h-full flex flex-col"
                  onClick={() => navigate(`/blog/${post.id}`)}
                >
                  <div className="h-24 sm:h-32 overflow-hidden">
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-3 sm:p-4 flex-grow flex flex-col">
                    <h3 className="text-sm sm:text-md font-semibold mb-2 hover:text-cyan-400 transition-colors duration-300 line-clamp-2 flex-grow">
                      {post.title}
                    </h3>
                    <div className="flex text-xs text-gray-400 mt-auto">
                      <span className="flex items-center mr-3 sm:mr-4">
                        <Calendar size={10} className="mr-1 text-cyan-400" />
                        {post.date}
                      </span>
                      <span className="flex items-center">
                        <Clock size={10} className="mr-1 text-purple-400" />
                        {post.readTime} min
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer - Improved for mobile */}
      <footer className="py-6 sm:py-8 border-t border-gray-800">
  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
    <a 
      href="https://www.arxhr007.me/" 
      target="_blank" 
      rel="noopener noreferrer"
      className="flex flex-col md:flex-row justify-between items-center cursor-pointer"
    >
      <div className="mb-4 md:mb-0 text-center md:text-left">
        <span className="font-bold text-lg sm:text-xl text-cyan-400">
          ARXHR<span className="text-purple-400">007</span>
        </span>
        <p className="text-gray-400 text-xs sm:text-sm mt-1">
          Building secure and scalable web solutions
        </p>
      </div>
    </a>
    <div className="mt-6 sm:mt-8 text-center text-gray-500 text-xs sm:text-sm">
      © {new Date().getFullYear()} Arxhr007. All rights reserved.
    </div>
  </div>
</footer>

    </div>
  );
};

// Hook for scroll reveal animations
const useScrollReveal = (threshold = 0.1) => {
  const [elements, setElements] = useState([]);
  const observer = useRef(null);

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-reveal');
          observer.current.unobserve(entry.target);
        }
      });
    }, { threshold });

    elements.forEach(el => {
      if (el) observer.current.observe(el);
    });

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [elements, threshold]);

  const ref = (el) => {
    if (el && !elements.includes(el)) {
      setElements(prev => [...prev, el]);
    }
  };

  return ref;
};

export default BlogPost;